import { supabase } from '@/lib/supabase';
import { AssistedLivingScorecard, ScorecardLookupRequest } from '@/types/scorecard';

export class ScorecardService {
  /**
   * Lookup scorecard data for a facility
   */
  static async lookupScorecard(request: ScorecardLookupRequest): Promise<AssistedLivingScorecard | null> {
    try {
      let query = supabase
        .from('assisted_living_scorecards')
        .select('*');

      // Search by NPI first if available
      if (request.npi) {
        query = query.eq('npi', request.npi);
      } else {
        // Fallback to facility name search
        query = query.ilike('facility_name', `%${request.facilityName}%`);
        
        // Add county filter if available
        if (request.county) {
          query = query.eq('county', request.county);
        }
      }

      const { data, error } = await query.single();

      if (error) {
        console.log('No scorecard data found:', error.message);
        return null;
      }

      return data as AssistedLivingScorecard;
    } catch (error) {
      console.error('Error looking up scorecard:', error);
      return null;
    }
  }

  /**
   * Get all scorecards for admin dashboard
   */
  static async getAllScorecards(): Promise<AssistedLivingScorecard[]> {
    try {
      const { data, error } = await supabase
        .from('assisted_living_scorecards')
        .select('*')
        .order('facility_name');

      if (error) throw error;
      return data as AssistedLivingScorecard[];
    } catch (error) {
      console.error('Error fetching all scorecards:', error);
      return [];
    }
  }

  /**
   * Create or update scorecard data
   */
  static async upsertScorecard(scorecard: Partial<AssistedLivingScorecard> & { facility_name: string }): Promise<boolean> {
    try {
      const { error } = await supabase
        .from('assisted_living_scorecards')
        .upsert(scorecard, {
          onConflict: 'facility_name,npi'
        });

      if (error) throw error;
      return true;
    } catch (error) {
      console.error('Error upserting scorecard:', error);
      return false;
    }
  }

  /**
   * Scrape scorecard data from Minnesota website
   * This would be implemented as an edge function in production
   */
  static async scrapeScorecard(facilityName: string): Promise<AssistedLivingScorecard | null> {
    // For now, return mock data to demonstrate the feature
    // In production, this would call an edge function that scrapes the website
    
    try {
      // Mock scorecard data for demonstration
      const mockScorecard = {
        facility_name: facilityName,
        overall_rating: Math.floor(Math.random() * 5) + 1,
        resident_quality_of_life_rating: Math.floor(Math.random() * 5) + 1,
        family_satisfaction_rating: Math.floor(Math.random() * 5) + 1,
        resident_health_rating: Math.floor(Math.random() * 5) + 1,
        safety_rating: Math.floor(Math.random() * 5) + 1,
        staffing_rating: Math.floor(Math.random() * 5) + 1,
        complaints_count: Math.floor(Math.random() * 10),
        violations_count: Math.floor(Math.random() * 5),
        data_source: 'mn_assisted_living_report_card'
      };

      const success = await this.upsertScorecard(mockScorecard);
      if (success) {
        return await this.lookupScorecard({ facilityName });
      }
      
      return null;
    } catch (error) {
      console.error('Error scraping scorecard:', error);
      return null;
    }
  }
}